import { useMemo, useState } from 'react';
import { ChevronDown, Search, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { AutoLabelBadge } from '@/components/AutoLabelBadge';
import { RealtimeStatusPill } from '@/components/RealtimeStatusPill';
import type { LabelFilterOption } from './LabelFilterBar';
import { useAccountStore } from '@/stores/accountStore';
import { useFolderStore } from '@/stores/folderStore';
import { useUIStore } from '@/stores/uiStore';
import { useCommandPaletteStore } from '@/stores/commandPaletteStore';
import { UNIFIED_ACCOUNT_ID } from '@/constants/mailbox';
import { cn } from '@/lib/utils';

interface ListHeaderProps {
  // Mobile label filter props
  labelFilterLabels?: LabelFilterOption[];
  labelFilterActiveLabelId?: string | 'all';
  labelFilterNoLabelOption?: LabelFilterOption | null;
  onLabelFilterChange?: (value: string | 'all') => void;
}

/**
 * The mobile list header: folder title + scope line (opens the scope sheet),
 * label filter, search. Desktop chrome lives in the full-width
 * WorkspaceHeader now, so this renders mobile-only.
 */
export function ListHeader({
  labelFilterLabels,
  labelFilterActiveLabelId,
  labelFilterNoLabelOption,
  onLabelFilterChange,
}: ListHeaderProps) {
  const openCommandPalette = useCommandPaletteStore((state) => state.open);
  const [labelPopoverOpen, setLabelPopoverOpen] = useState(false);

  const hasLabelFilter = labelFilterLabels && onLabelFilterChange;
  const isLabelFilterActive = labelFilterActiveLabelId && labelFilterActiveLabelId !== 'all';

  const handleLabelSelect = (labelId: string) => {
    onLabelFilterChange?.(labelId);
    setLabelPopoverOpen(false);
  };

  const renderLabelFilterButton = (labelId: string, labelName: string, color?: string | null) => {
    const isActive = labelFilterActiveLabelId === labelId;
    const buttonClass = cn(
      'inline-flex items-center rounded-full border px-0.5 py-0.5 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
      isActive
        ? 'border-primary/60 border-2 bg-primary/10 shadow-sm'
        : 'border-transparent hover:bg-muted/60 hover:opacity-100 opacity-70'
    );
    return (
      <button
        key={labelId}
        type="button"
        className={buttonClass}
        onClick={() => handleLabelSelect(labelId)}
      >
        <AutoLabelBadge
          label={{ id: labelId, name: labelName, color: color ?? null }}
          className=""
        />
      </button>
    );
  };

  return (
    // surface-dark: the mobile header joins the plum chrome (status-bar band
    // above, dock below) — the token remap keeps all the text/buttons legible.
    <div className="surface-dark px-4 pb-2 pt-2 md:hidden">
      <div className="flex items-center justify-between gap-2">
        <MobileScopeTitle />

        <div className="ml-auto flex shrink-0 items-center gap-1">
          <RealtimeStatusPill />

          {hasLabelFilter && (
            <Popover open={labelPopoverOpen} onOpenChange={setLabelPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant={isLabelFilterActive ? 'secondary' : 'ghost'}
                  size="sm"
                  className={cn(
                    'h-8 w-8 p-0 rounded-full relative',
                    isLabelFilterActive
                      ? 'bg-background shadow-sm text-primary'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                  aria-label="Filter by label"
                >
                  <Tag className="h-4 w-4" />
                  {isLabelFilterActive && (
                    <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-primary" />
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-64 p-2">
                <div className="flex flex-wrap gap-1">
                  {renderLabelFilterButton('all', 'all', null)}
                  {labelFilterNoLabelOption ? renderLabelFilterButton(labelFilterNoLabelOption.id, labelFilterNoLabelOption.name, labelFilterNoLabelOption.color) : null}
                  {labelFilterLabels.length > 0 && <div className="w-full h-px bg-border my-1" />}
                  {labelFilterLabels.map((label) => renderLabelFilterButton(label.id, label.name, label.color))}
                </div>
              </PopoverContent>
            </Popover>
          )}

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => openCommandPalette()}
            className="h-8 w-8 rounded-full p-0 text-muted-foreground hover:text-foreground"
            aria-label="Search"
          >
            <Search className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Mobile header identity: the current folder as title with the scope line
 * ("All accounts · 12 unread") beneath it. Tapping opens the scope sheet —
 * the account/folder/layout picker that replaced the sidebar drawer.
 */
function MobileScopeTitle() {
  const mailView = useUIStore((state) => state.mailView);
  const setScopeSheetOpen = useUIStore((state) => state.setScopeSheetOpen);
  const accounts = useAccountStore((state) => state.accounts);
  const selectedAccountId = useAccountStore((state) => state.selectedAccountId);
  const folders = useFolderStore((state) => state.folders);
  const selectedFolderByAccount = useFolderStore((state) => state.selectedFolderByAccount);

  const selectedFolderId = selectedAccountId
    ? selectedFolderByAccount[selectedAccountId] ?? ''
    : '';

  const { title, scopeLine } = useMemo(() => {
    if (mailView === 'briefing') {
      return { title: 'Briefing', scopeLine: null };
    }
    const folder = folders.find((f) => f.id === selectedFolderId);
    const account = accounts.find((a) => a.id === selectedAccountId);
    const accountLabel =
      !account || account.id === UNIFIED_ACCOUNT_ID
        ? 'All accounts'
        : account.displayName || account.emailAddress;
    const unread = folder?.count ?? folder?.unreadCount ?? 0;
    return {
      title: folder?.name ?? 'Inbox',
      scopeLine: unread > 0 ? `${accountLabel} · ${unread} unread` : accountLabel,
    };
  }, [mailView, folders, accounts, selectedAccountId, selectedFolderId]);

  return (
    <button
      type="button"
      className="min-w-0 flex-1 text-left"
      onClick={() => setScopeSheetOpen(true)}
      disabled={mailView === 'briefing'}
      aria-label="Change account or folder"
    >
      <div className="truncate text-lg font-bold leading-tight">{title}</div>
      {scopeLine && (
        <div className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
          <span className="truncate">{scopeLine}</span>
          <ChevronDown className="h-3 w-3 shrink-0 opacity-70" />
        </div>
      )}
    </button>
  );
}
